import React from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import { TextField } from './TextField';
import { TextareaField } from './TextareaField';
import { EmailField } from './EmailField';
import { SelectField } from './SelectField';
import { PhoneField } from './PhoneField';
import { SectionField } from './SectionField';
import { RecaptchaField } from './RecaptchaField';
import { RadioField } from './RadioField';
import { DatePickerField } from './DatePickerField';
import { NumberField } from './NumberField';
import { PostcodeField } from './PostcodeField';
import { IconTypes, SvgIconSize } from 'helpers/SvgIcon';
import { CheckboxField } from './CheckboxField';
import { HiddenField } from './HiddenField';
import { HeaderField } from './HeaderField';

export function renderFormField(
  field: CustomForm.FormField_Item,
  errors?: FormikErrors<Record<string, string>>,
  touched?: FormikTouched<Record<string, string>>,
  reCAPTCHASiteKey?: string,
  value?: string,
  onChange?: (value: string) => void,
  onBlur?: () => void,
  options?: {
    hideLabel?: boolean | undefined;
    isStandalone?: boolean | undefined;
    prefixText?: string | undefined;
    prefixIcon?: string | undefined;
    suffixIcon?: string | undefined;
    prefixIconAriaLabel?: string | undefined;
    suffixIconAriaLabel?: string | undefined;
  },
  fieldIcon?: IconTypes,
  fieldIconSize?: SvgIconSize,
  maxDigits?: number,
  colCount?: number,
  hideLabel?: boolean
): React.ReactElement | null {
  if (!field.fields?.fieldType?.value) return null;

  const hideLabelInternal = options?.hideLabel || hideLabel || false;

  const fieldType = field.fields.fieldType.value.toLowerCase();

  // Handle section fields (no individual fieldName, contains multiple fields)
  if (fieldType === 'section') {
    return (
      <SectionField
        key={field.id || 'section'}
        field={field}
        reCAPTCHASiteKey={reCAPTCHASiteKey}
        colCount={colCount}
      />
    );
  }
  if (fieldType === 'postcode') {
    return <PostcodeField key={field.id || 'postcode'} field={field} />;
  }

  // For non-section fields, fieldName is required
  if (!field.fields?.fieldName?.value) return null;

  const fieldName = field.fields.fieldName.value;
  const isTouched = touched?.[fieldName];
  const hasError = (errors?.[fieldName] && isTouched) || false;

  // Determine isStandalone: use explicit option if provided
  const isStandalone = options?.isStandalone;

  switch (fieldType) {
    case 'text':
    case 'single-line text':
      return (
        <TextField
          key={fieldName}
          field={field}
          hasError={hasError}
          isTouched={isTouched}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          errors={errors as Record<string, string | undefined>}
          touched={touched as Record<string, boolean | undefined>}
          hideLabel={hideLabelInternal}
          isStandalone={isStandalone}
          prefixText={options?.prefixText}
          prefixIcon={options?.prefixIcon}
          prefixIconAriaLabel={options?.prefixIconAriaLabel}
          suffixIcon={options?.suffixIcon}
          suffixIconAriaLabel={options?.suffixIconAriaLabel}
        />
      );

    case 'hidden':
      return (
        <HiddenField
          key={fieldName}
          field={field}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          isStandalone={isStandalone}
        />
      );

    case 'header':
      return (
        <HeaderField
          key={fieldName}
          field={field}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          isStandalone={isStandalone}
        />
      );

    case 'number':
      return (
        <NumberField
          key={fieldName}
          field={field}
          hasError={hasError}
          isTouched={isTouched}
          fieldIcon={fieldIcon}
          fieldIconSize={fieldIconSize}
          maxDigits={maxDigits}
        />
      );

    case 'email':
      return (
        <EmailField
          key={fieldName}
          field={field}
          hasError={hasError}
          isTouched={isTouched}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          errors={errors as Record<string, string | undefined>}
          touched={touched as Record<string, boolean | undefined>}
          hideLabel={hideLabelInternal}
          isStandalone={isStandalone}
        />
      );

    case 'textarea':
    case 'multi-line text':
    case 'multiline':
      return (
        <TextareaField
          key={fieldName}
          field={field}
          hasError={hasError}
          isTouched={isTouched}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          errors={errors as Record<string, string | undefined>}
          touched={touched as Record<string, boolean | undefined>}
          hideLabel={hideLabelInternal}
          isStandalone={isStandalone}
        />
      );

    case 'select':
    case 'dropdown':
      return (
        <SelectField
          key={fieldName}
          field={field}
          hasError={hasError}
          isTouched={isTouched}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          errors={errors as Record<string, string | undefined>}
          touched={touched as Record<string, boolean | undefined>}
          hideLabel={hideLabelInternal}
          isStandalone={isStandalone}
        />
      );

    case 'phone':
    case 'tel':
    case 'telephone':
      return <PhoneField key={fieldName} field={field} hasError={hasError} />;

    case 'radio':
    case 'radio button':
    case 'radiobutton':
      return (
        <RadioField
          key={fieldName}
          field={field}
          hasError={hasError}
          isTouched={isTouched}
          errors={errors as Record<string, string | undefined>}
          touched={touched as Record<string, boolean | undefined>}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          hideLabel={hideLabelInternal}
          isStandalone={isStandalone}
        />
      );

    case 'checkbox':
    case 'checkbox button':
    case 'checkboxes':
      return (
        <CheckboxField
          key={fieldName}
          field={field}
          hasError={hasError}
          isTouched={isTouched}
          errors={errors as Record<string, string | undefined>}
          touched={touched as Record<string, boolean | undefined>}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          hideLabel={hideLabelInternal}
          isStandalone={isStandalone}
        />
      );

    case 'recaptcha':
      return (
        <RecaptchaField
          key={fieldName}
          field={field}
          hasError={hasError}
          isTouched={isTouched}
          reCAPTCHASiteKey={reCAPTCHASiteKey}
        />
      );

    case 'date':
    case 'datepicker':
    case 'date-picker':
      return (
        <DatePickerField
          key={fieldName}
          field={field}
          hasError={hasError}
          isTouched={isTouched}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          errors={errors as Record<string, string | undefined>}
          touched={touched as Record<string, boolean | undefined>}
          hideLabel={hideLabelInternal}
          isStandalone={isStandalone}
        />
      );

    default:
      console.warn(`Unsupported field type: ${fieldType}`);
      return null;
  }
}
